import React from 'react';
import { Handle, Position } from 'reactflow';
import NodeSymbolSelect from './NodeSymbolSelect';

const indicatorOptions = [
  { value: 'sma', label: 'SMA (Simple Moving Average)' },
  { value: 'ema', label: 'EMA (Exponential Moving Average)' },
  { value: 'rsi', label: 'RSI (Relative Strength Index)' },
  { value: 'bollinger', label: 'Bollinger Bands' },
  { value: 'macd', label: 'MACD' },
  { value: 'atr', label: 'ATR (Average True Range)' },
  { value: 'stochastic', label: 'Stochastic' },
  { value: 'vwap', label: 'VWAP (Volume Weighted Avg Price)' },
  { value: 'adx', label: 'ADX (Average Directional Index)' },
  { value: 'cci', label: 'CCI (Commodity Channel Index)' },
  { value: 'williams_r', label: 'Williams %R' },
  { value: 'roc', label: 'ROC (Rate of Change)' },
  { value: 'momentum', label: 'Momentum' },
  { value: 'trix', label: 'TRIX' },
  { value: 'vortex', label: 'Vortex Indicator' },
  { value: 'dpo', label: 'DPO (Detrended Price Oscillator)' },
  { value: 'supertrend', label: 'Supertrend' },
];

const vwapBandOptions = [
  { value: 'mid', label: 'VWAP (mid)' },
  { value: 'upper_1', label: '+1σ band' },
  { value: 'upper_2', label: '+2σ band' },
  { value: 'lower_1', label: '−1σ band' },
  { value: 'lower_2', label: '−2σ band' },
];

export default function IndicatorNode({ data, id }) {
  const onChange = (field, value) => {
    if (data.onChange) data.onChange(id, field, value);
  };

  const indicator = data.indicator || 'sma';
  const isVwap = indicator === 'vwap';
  const isSupertrend = indicator === 'supertrend';

  return (
    <div style={{
      padding: 12,
      borderRadius: 8,
      border: '2px solid #6366f1',
      background: 'var(--card)',
      minWidth: 200,
      fontSize: 13,
      color: 'var(--text)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: '#818cf8' }}>
        📊 Indicator
      </div>
      <NodeSymbolSelect value={data.symbol} onChange={(v) => onChange('symbol', v)} />
      <select
        value={indicator}
        onChange={(e) => onChange('indicator', e.target.value)}
        style={{ width: '100%', marginBottom: 6, padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4 }}
      >
        {indicatorOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {isVwap ? (
        <>
          <select
            value={data.band || 'mid'}
            onChange={(e) => onChange('band', e.target.value)}
            style={{ width: '100%', marginBottom: 6, padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4 }}
          >
            {vwapBandOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {(data.band || 'mid') !== 'mid' && (
            <div>
              <label style={{ fontSize: 11, color: 'var(--muted)' }}>σ ×: </label>
              <input
                type="number"
                min={0.1}
                max={5}
                step={0.1}
                value={data.stddev_mult ?? 1}
                onChange={(e) => onChange('stddev_mult', Math.max(0.1, Math.min(5, parseFloat(e.target.value) || 1)))}
                style={{ width: 60, padding: 2, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-mono)' }}
              />
            </div>
          )}
          <div style={{ marginTop: 6, fontSize: 10, color: 'var(--muted)', lineHeight: 1.4 }}>
            Session-anchored — intraday intervals only. Wire several VWAP nodes (one per band) to build a reversal setup.
          </div>
        </>
      ) : (
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted)' }}>Period: </label>
          <input
            type="number"
            value={data.params?.period ?? (isSupertrend ? 10 : 20)}
            onChange={(e) => onChange('params', { ...data.params, period: parseInt(e.target.value) || (isSupertrend ? 10 : 20) })}
            style={{ width: 60, padding: 2, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-mono)' }}
            min={1}
          />
          {isSupertrend && (
            <>
              <label style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 8 }}>Multiplier ×: </label>
              <input
                type="number"
                min={0.5}
                max={20}
                step={0.5}
                value={data.params?.multiplier ?? 3}
                onChange={(e) => onChange('params', { ...data.params, multiplier: Math.max(0.5, Math.min(20, parseFloat(e.target.value) || 3)) })}
                style={{ width: 60, padding: 2, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-mono)' }}
              />
              <div style={{ marginTop: 6, fontSize: 10, color: 'var(--muted)', lineHeight: 1.4 }}>
                Trend band. For a long signal: price(close) <strong>crosses above</strong> supertrend; short: <strong>crosses below</strong>.
              </div>
            </>
          )}
        </div>
      )}
      <Handle type="source" position={Position.Right} id="output" style={{ background: '#6366f1' }} />
    </div>
  );
}
